/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */



import { Directive, Input, ElementRef, Renderer, HostListener, Host, Optional } from '@angular/core';
import { AfterViewInit, AfterContentInit, ContentChild, Output, EventEmitter } from '@angular/core';
import { placements } from './placements';

import { Menu, ClickParam } from '../menu/menu';
import { DropdownButton } from './dropdown.button';
import { DomUtils } from '../util/dom.utils';
import { onNextFrame } from '../util/anim.frame';
import { getPopupClassNameFromAlign, getAlignFromPlacement } from '../trigger/utils';
import align from 'dom-align';


export type DropdownPlacement = 'topLeft' | 'topCenter' | 'topRight' | 'bottomLeft' | 'bottomCenter' | 'bottomRight';
export type DropdownTriggerType = 'click' | 'hover';
/**
 * Dropdown - 下拉菜单
 */
export class Dropdown implements AfterContentInit, AfterViewInit {
/**
 * 样式前缀
 */
dropdownPrefixCls = 'ant-dropdown';
/**
 * 触发下拉的行为，可选取值：Array<'click'|'hover'>
 */
triggerAction: DropdownTriggerType | DropdownTriggerType[] = ['hover'];
/**
 * 触发标签的selector，默认为空，表示当前标签
 */
triggerAt: string;
/**
 * 菜单弹出位置：`bottomLeft` `bottomCenter` `bottomRight` `topLeft` `topCenter` `topRight`
 */
dropdownPlacement: DropdownPlacement = 'bottomLeft';
/**
 * 位置信息
 */
builtinPlacements: Object = placements;
/**
 * 下拉菜单的最小宽度与触发的组件宽度一致
 */
dropdownMatchTriggerWidth = true;
/**
 * 缩略写法：action placement
 * @param {?} dropdown
 * @return {?}
 */
set dropdown(dropdown: string) {
        if (dropdown && dropdown.length) {
            const /** @type {?} */ str = dropdown.split('[ ,]');

            const /** @type {?} */ triggerAction: DropdownTriggerType[] = [];
            for (let /** @type {?} */ i = 0; i < str.length; i++) {
                if (str[i] === 'click' || str[i] === 'hover') {
                    triggerAction.push( /** @type {?} */((str[i] as DropdownTriggerType)));
                }
                if (['topLeft', 'topCenter', 'topRight', 'bottomLeft', 'bottomCenter', 'bottomRight'].includes(str[i])) {
                    this.dropdownPlacement = /** @type {?} */(( str[i] as DropdownPlacement));
                }
            }
            if (triggerAction.length) {
                this.triggerAction = triggerAction;
            }
        }
    }
/**
 * 一个钩子函数: 默认点击菜单后将关闭菜单，如果此函数返回false，将停止关闭菜单。
 */
menuClickHanlder: (event: any) => boolean;
/**
 * 是否禁用状态，默认为 false
 */
disabled = false;
/**
 * 鼠标移入后显示菜单的延迟时间（秒）
 */
mouseEnterDelay = 0.15;
/**
 * 鼠标移出后隐藏菜单的延迟时间（秒）
 */
mouseLeaveDelay = 0.15;
/**
 * 菜单
 */
menu: Menu;
/**
 * 菜单显示事件
 */
menuVisibleChange = new EventEmitter<boolean>();
/**
 * 菜单显示或隐藏相关
 */
private popupVisible = false;
private popupContainer: HTMLElement;
private delayTimer: any;
private alignClass: string;
private clickOutsideHandler: Function;
private touchOutsideHandler: Function;
/**
 * @param {?} renderer
 * @param {?} elementRef
 * @param {?} dropdownButton
 */
constructor(
private renderer: Renderer,
private elementRef: ElementRef,
private dropdownButton: DropdownButton) {

    }
/**
 * @return {?}
 */
ngAfterContentInit(): void {
        if (!this.menu && this.dropdownButton) {
            this.menu = this.dropdownButton.menu;
        }
        if (!this.menu) {
            throw new Error('must contain a Menu...');
        }

        onNextFrame(() => {
            this.menu.visible = false;
            this.menu.prefixCls = `${this.dropdownPrefixCls}-menu`;
        });

        // 订阅菜单的Click事件
        this.menu.menuClick.subscribe((event: ClickParam) => this.onMenuClick(event));
    }
/**
 * @param {?} event
 * @return {?}
 */
onMenuClick(event: ClickParam): void {
        // 可以通过注入的menuClickHanlder钩子阻止菜单的消失
        if (this.menuClickHanlder && this.menuClickHanlder(event) === false) {
            return;
        }

        // 也可以通过原生Event的preventDefault属性来阻止菜单的消失
        const /** @type {?} */ domEvent: Event = event.domEvent;
        if (domEvent && domEvent.defaultPrevented) {
            return;
        }
        // 菜单隐藏
        this.delaySetPopupVisible(false, 0.1);
    }
/**
 * @return {?}
 */
ngAfterViewInit(): void {
        this.renderer.setElementClass(
            this.elementRef.nativeElement, `${this.dropdownPrefixCls}-trigger`, true);

        // 对按钮(ant-button)作特殊处理
        if (this.elementRef.nativeElement.nodeName === 'ANT-BUTTON') {
            this.triggerAt = this.triggerAt || 'button';
        }

        // 将菜单移入绝对定位的Element内部
        // <div style="position: absolute; top: 0px; left: 0px; width: 100%;">
        const /** @type {?} */ container = this.getPopupContainer();
        container.appendChild(this.getMenuElement());
    }
/**
 * @return {?}
 */
protected getPopupContainer(): HTMLElement {
        if (!this.popupContainer) {
            const /** @type {?} */ popupContainer = document.createElement('div');
            // Make sure default popup container will never cause scrollbar appearing
            // https://github.com/react-component/trigger/issues/41
            // popupContainer.style.position = 'absolute';
            // popupContainer.style.top = '0';
            // popupContainer.style.left = '0';
            // popupContainer.style.width = '100%';
            popupContainer.className = this.dropdownPrefixCls;

            // const mountNode = this.getPopupContainer() || getDocument().body;
            const /** @type {?} */ mountNode = this.getHostElement();
            mountNode.appendChild(popupContainer);
            this.popupContainer = popupContainer;
        }

        return this.popupContainer;
    }
/**
 * The underlying host native element
 * @return {?}
 */
getHostElement(): HTMLElement {
        return /** @type {?} */(( this.elementRef.nativeElement as HTMLElement));
    }
/**
 * @return {?}
 */
getTiggerElement(): HTMLElement {
        const /** @type {?} */ host: HTMLElement = this.getHostElement();
        return this.triggerAt ? /** @type {?} */(( host.querySelector(this.triggerAt) as HTMLElement)) : host;
    }
/**
 * @return {?}
 */
getMenuElement(): HTMLElement {
        return this.menu.getHostElement();
    }
/**
 * @param {?} event
 * @return {?}
 */
onTriggerClick(event: MouseEvent): void {
        if (this.disabled) {
            return;
        }
        if (this.isClickTiggerAction()) {
            this.delaySetPopupVisible(!this.popupVisible, 0.1);
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
onTriggerMouseEnter(event: MouseEvent): void {
        if (this.disabled) {
            return;
        }
        if (this.isPointerTiggerAction()) {
            this.delaySetPopupVisible(true, this.mouseEnterDelay);
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
onTriggerMouseLeave(event: MouseEvent): void {
        if (this.disabled) {
            return;
        }
        if (!this.isPopupVisible()) {
            return;
        }
        if (this.isPointerTiggerAction()) {
            const /** @type {?} */ container = this.getPopupContainer();
            if (DomUtils.contains(container, /** @type {?} */(( event.relatedTarget as Node)))
                || DomUtils.contains(container, /** @type {?} */(( event.target as Node)))) {
                return; // 还在菜单内部
            }
            this.delaySetPopupVisible(false, this.mouseLeaveDelay);
        }
    }
/**
 * @return {?}
 */
isClickTiggerAction() {
        if (typeof this.triggerAction === 'string') {
            return this.triggerAction === 'click';
        }
        return this.triggerAction.indexOf('click') !== -1;
    }
/**
 * @return {?}
 */
isPointerTiggerAction() {
        if (typeof this.triggerAction === 'string') {
            return this.triggerAction === 'hover';
        }
        return this.triggerAction.indexOf('hover') !== -1;
    }
/**
 * @return {?}
 */
getAlign(): {} {
        return getAlignFromPlacement(this.builtinPlacements, this.dropdownPlacement, {});
    }
/**
 * @return {?}
 */
forceAlign(): void {
        if (!this.disabled) {
            const /** @type {?} */ baseTarget = this.getTiggerElement(); // 基准组件，位置不会变
            const /** @type {?} */ alignTarget = this.getPopupContainer();

            // https://www.npmjs.com/package/dom-align
            const /** @type {?} */ alignConfig = this.getAlign();
            this.onAlign(align(alignTarget, baseTarget, alignConfig));
        }
    }
/**
 * @param {?} align
 * @return {?}
 */
onAlign(align: any): void {
        const /** @type {?} */ alignClass = getPopupClassNameFromAlign(
            this.builtinPlacements, this.dropdownPrefixCls, align);

        // 先删除旧的样式，再添加新的样式
        if (this.alignClass && this.alignClass !== alignClass) {
            this.renderer.setElementClass(this.getPopupContainer(), this.alignClass, false);
            this.renderer.setElementClass(this.getPopupContainer(), alignClass, true);
            this.alignClass = alignClass;
        }
    }
/**
 * 显示或者隐藏菜单
 * 
 * @param {?} visible true-显示，false-隐藏
 * @param {?} delayS 延迟时间（单位：秒）
 * @return {?}
 */
delaySetPopupVisible(visible: boolean, delayS: number): void {
        const /** @type {?} */ delay: number = delayS * 1000;
        this.clearDelayTimer();
        if (delay) {
            this.delayTimer = setTimeout(() => {
                this.setPopupVisible(visible);
                this.clearDelayTimer();
            }, delay);
        } else {
            this.setPopupVisible(visible);
        }
    }
/**
 * @return {?}
 */
isPopupVisible(): boolean {
        return this.popupVisible;
    }
/**
 * @param {?} popupVisible
 * @return {?}
 */
setPopupVisible(popupVisible: boolean): void {
        if (this.disabled) {
            return;
        }

        if (this.popupVisible !== popupVisible) {
            this.popupVisible = popupVisible;
            this.menu.visible = popupVisible;

            // We must listen to `mousedown` or `touchstart`, edge case:
            // https://github.com/ant-design/ant-design/issues/5804
            // https://github.com/react-component/calendar/issues/250
            // https://github.com/react-component/trigger/issues/50
            if (popupVisible) {
                if (!this.clickOutsideHandler) {
                    this.clickOutsideHandler = this.renderer.listen('document', 'mousedown', this.onDocumentClick.bind(this));
                }
                // always hide on mobile
                if (!this.touchOutsideHandler) {
                    this.touchOutsideHandler = this.renderer.listen('document', 'touchstart', this.onDocumentClick.bind(this));
                }
            }
            if (!popupVisible) {
                this.clearOutsideHandler();
            }

            this.menuVisibleChange.emit(popupVisible);
            this.afterPopupVisibleChange(popupVisible);
        }
    }
/**
 * @param {?} popupVisible
 * @return {?}
 */
afterPopupVisibleChange(popupVisible: boolean): void {
        if (popupVisible) {
            onNextFrame(() => {
                this.forceAlign();

                // calc dropdownMatchTriggerWidth
                if (this.dropdownMatchTriggerWidth) {
                    const /** @type {?} */ hostEl: HTMLElement = this.getHostElement();
                    const /** @type {?} */ popuEl: HTMLElement = this.getMenuElement();
                    // const width = this.dropdownMatchTriggerWidth ? 'width' : 'minWidth';
                    this.renderer.setElementStyle(popuEl, 'minWidth', `${hostEl.offsetWidth}px`);
                }
            });
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
onDocumentClick(event: MouseEvent): void {
        const /** @type {?} */ target = /** @type {?} */(( event.target as Node));
        const /** @type {?} */ root = this.getHostElement();
        const /** @type {?} */ popupEl = this.getMenuElement();
        if (!DomUtils.contains(root, target) && !DomUtils.contains(popupEl, target)) {
            this.setPopupVisible(false);
        }
    }
/**
 * @return {?}
 */
clearOutsideHandler() {
        if (this.clickOutsideHandler) {
            this.clickOutsideHandler(); // Removes "listen" listener
            this.clickOutsideHandler = null;
        }

        if (this.touchOutsideHandler) {
            this.touchOutsideHandler(); // Removes "listen" listener
            this.touchOutsideHandler = null;
        }
    }
/**
 * @return {?}
 */
clearDelayTimer() {
        if (this.delayTimer) {
            clearTimeout(this.delayTimer);
            this.delayTimer = null;
        }
    }

static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
    selector: '[dropdown]'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
{type: DropdownButton, decorators: [{ type: Optional }, { type: Host }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'dropdownPrefixCls': [{ type: Input },],
'triggerAction': [{ type: Input },],
'triggerAt': [{ type: Input },],
'dropdownPlacement': [{ type: Input },],
'builtinPlacements': [{ type: Input },],
'dropdownMatchTriggerWidth': [{ type: Input },],
'dropdown': [{ type: Input },],
'menuClickHanlder': [{ type: Input },],
'disabled': [{ type: Input },],
'mouseEnterDelay': [{ type: Input },],
'mouseLeaveDelay': [{ type: Input },],
'menu': [{ type: ContentChild, args: [Menu, ] },],
'menuVisibleChange': [{ type: Output },],
'onTriggerClick': [{ type: HostListener, args: ['click', ['$event'], ] },],
'onTriggerMouseEnter': [{ type: HostListener, args: ['mouseenter', ['$event'], ] },],
'onTriggerMouseLeave': [{ type: HostListener, args: ['mouseleave', ['$event'], ] },],
};
}

function Dropdown_tsickle_Closure_declarations() {
/** @type {?} */
Dropdown.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Dropdown.ctorParameters;
/** @type {?} */
Dropdown.propDecorators;
/**
 * 样式前缀
 * @type {?}
 */
Dropdown.prototype.dropdownPrefixCls;
/**
 * 触发下拉的行为，可选取值：Array<'click'|'hover'>
 * @type {?}
 */
Dropdown.prototype.triggerAction;
/**
 * 触发标签的selector，默认为空，表示当前标签
 * @type {?}
 */
Dropdown.prototype.triggerAt;
/**
 * 菜单弹出位置：`bottomLeft` `bottomCenter` `bottomRight` `topLeft` `topCenter` `topRight`
 * @type {?}
 */
Dropdown.prototype.dropdownPlacement;
/**
 * 位置信息
 * @type {?}
 */
Dropdown.prototype.builtinPlacements;
/**
 * 下拉菜单的最小宽度与触发的组件宽度一致
 * @type {?}
 */
Dropdown.prototype.dropdownMatchTriggerWidth;
/**
 * 一个钩子函数: 默认点击菜单后将关闭菜单，如果此函数返回false，将停止关闭菜单。
 * @type {?}
 */
Dropdown.prototype.menuClickHanlder;
/**
 * 是否禁用状态，默认为 false
 * @type {?}
 */
Dropdown.prototype.disabled;
/**
 * 鼠标移入后显示菜单的延迟时间（秒）
 * @type {?}
 */
Dropdown.prototype.mouseEnterDelay;
/**
 * 鼠标移出后隐藏菜单的延迟时间（秒）
 * @type {?}
 */
Dropdown.prototype.mouseLeaveDelay;
/**
 * 菜单
 * @type {?}
 */
Dropdown.prototype.menu;
/**
 * 菜单显示事件
 * @type {?}
 */
Dropdown.prototype.menuVisibleChange;
/**
 * 菜单显示或隐藏相关
 * @type {?}
 */
Dropdown.prototype.popupVisible;
/** @type {?} */
Dropdown.prototype.popupContainer;
/** @type {?} */
Dropdown.prototype.delayTimer;
/** @type {?} */
Dropdown.prototype.alignClass;
/** @type {?} */
Dropdown.prototype.clickOutsideHandler;
/** @type {?} */
Dropdown.prototype.touchOutsideHandler;
/** @type {?} */
Dropdown.prototype.renderer;
/** @type {?} */
Dropdown.prototype.elementRef;
/** @type {?} */
Dropdown.prototype.dropdownButton;
}



interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
